import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  isEntryEditable,
  type Entry,
  type Membership,
  type Movement,
  type Prediction,
  type Round,
  type Sport
} from '@h2h/shared';
import { useLeagueRounds, useMyEntry, useMyGroup, useMyStanding, useRoundFixtures, useSubmitCyclingEntry, useEnrichedCyclingFixtures } from '../../../hooks/useQueries';
import { CYCLING_WEIGHTS, toDuelPicks } from '../../../lib/cyclingPicks';
import { cyclingHubPickHref, footballHubPickHref } from '../../../lib/fixtureRoute';
import { useHaptics } from '../../../hooks/useHaptics';
import { DesktopColumns } from '../../../components/ui/DesktopColumns';
import { LiveDuelSheet } from '../../match-detail/LiveDuelSheet';
import { LiveFixtureSheet } from '../../match-detail/LiveFixtureSheet';
import type { Tab } from '../../round-play/types';
import { useNow } from '../helpers';
import { MAX_PEEK_ROUNDS } from './constants';
import { CompeteLadderSnap } from './CompeteLadderSnap';
import { CyclingStageCard } from './CyclingStageCard';
import { PicksCard } from './PicksCard';
import { SlatePager } from './SlatePager';
import { BuildOverview, CyclingPicksSheet } from './build';
import type { PicksCtaAction } from './types';

/**
 * Compete tab — BYC-first league home.
 *
 *   1. Play card   — the only hero: today's slate, card builder CTA, fixture preview.
 *   2. Ladder snap — only after you've entered: rank, round pts, rivals around you.
 *
 * Fixtures, real table, and full division history live on the other tabs — not duplicated here.
 */
export function CompeteTab({
  leagueId,
  sport = 'FOOTBALL',
  nationalTeams = false,
  scorerEnabled = true,
  statsEnabled = false,
  round,
  entry,
  membership: _membership,
  uid,
  tierIndex,
  onPredict,
  onOpenFixture: _onOpenFixture,
  onFullTable,
  onOpenTab: _onOpenTab
}: {
  leagueId: string;
  sport?: Sport;
  nationalTeams?: boolean;
  scorerEnabled?: boolean;
  statsEnabled?: boolean;
  round: Round | null | undefined;
  entry: Entry | null | undefined;
  membership: Membership | null | undefined;
  uid: string | null;
  tierIndex: number | null;
  onPredict: (action: PicksCtaAction, roundId?: string, hasEntry?: boolean) => void;
  onOpenFixture: (fixtureId: string, roundId?: string) => void;
  onInvite: () => void;
  onFullTable: () => void;
  onOpenTab: (tab: Tab) => void;
}) {
  const isCycling = sport === 'CYCLING';
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const fixtureFocus = params.get('fixture');
  const duelFocus = params.get('duel');
  const urlRound = params.get('round');
  const { impact } = useHaptics();
  const submitCycling = useSubmitCyclingEntry(uid);
  const currentRoundId = round?.roundId ?? null;
  const groupQuery = useMyGroup(leagueId, currentRoundId, uid, true);
  const group = groupQuery.data;
  const { data: myStand } = useMyStanding(leagueId, currentRoundId, uid);
  const { data: rounds } = useLeagueRounds(leagueId);
  const now = useNow();

  const settled = round?.status === 'SETTLED';
  const liveRound =
    round?.status === 'OPEN' || round?.status === 'LOCKING' || round?.status === 'SETTLING';

  const { data: curFixtures } = useRoundFixtures(leagueId, currentRoundId, liveRound);
  const liveSettledCount = (curFixtures ?? []).filter(
    (f) => f.status === 'SETTLED' || f.status === 'VOID'
  ).length;
  const liveProgress =
    liveRound && (curFixtures?.length ?? 0) > 0
      ? { settled: liveSettledCount, total: curFixtures!.length }
      : null;

  const selectableRounds = useMemo(() => {
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);
    const startMs = startOfToday.getTime();
    const byId = new Map<string, Round>();
    if (round) byId.set(round.roundId, round);
    for (const r of rounds ?? []) {
      const upcomingStatus = r.status === 'SCHEDULED' || r.status === 'OPEN' || r.status === 'LOCKING';
      if (upcomingStatus && (r.firstKickoffAt >= startMs || r.roundId === currentRoundId)) {
        byId.set(r.roundId, r);
      }
    }
    return [...byId.values()].sort((a, b) => a.firstKickoffAt - b.firstKickoffAt).slice(0, MAX_PEEK_ROUNDS);
  }, [rounds, round, currentRoundId, now]);

  const [pickedRoundId, setPickedRoundId] = useState<string | null>(null);
  const [liveFixtureId, setLiveFixtureId] = useState<string | null>(null);
  useEffect(() => setPickedRoundId(null), [currentRoundId]);
  useEffect(() => {
    if (urlRound) setPickedRoundId(urlRound);
  }, [urlRound]);
  const selectedRoundId = pickedRoundId ?? currentRoundId ?? selectableRounds[0]?.roundId ?? null;
  const selectedRound = selectableRounds.find((r) => r.roundId === selectedRoundId) ?? round ?? null;

  const selOpen = selectedRound?.status === 'OPEN' || selectedRound?.status === 'LOCKING';
  const selSettled = selectedRound?.status === 'SETTLED';
  const selUpcoming = selectedRound?.status === 'SCHEDULED';

  // Cycling hub ladder needs fixtures for every round state (open, live, settled).
  const selFixturesLive = isCycling || selOpen || selectedRound?.status === 'SETTLING';
  const { data: selFixtures } = useRoundFixtures(leagueId, selectedRound?.roundId ?? null, selFixturesLive);
  const { data: selEntry } = useMyEntry(leagueId, selectedRound?.roundId ?? null, uid);

  const flexible = selectedRound?.pickMode === 'FLEXIBLE';
  const picksK = selectedRound?.picksRequired ?? 0;

  const slateFixtures = useMemo(
    () => [...(selFixtures ?? [])].sort((a, b) => a.kickoffAt - b.kickoffAt),
    [selFixtures]
  );
  const enrichedCyclingFixtures = useEnrichedCyclingFixtures(isCycling ? leagueId : undefined, isCycling ? slateFixtures : []);
  const cyclingSlateFixtures = isCycling ? (enrichedCyclingFixtures ?? slateFixtures) : slateFixtures;
  const predictionByFixture = useMemo(() => {
    const m = new Map<string, Prediction>();
    for (const p of selEntry?.predictions ?? []) m.set(p.fixtureId, p);
    return m;
  }, [selEntry]);

  const cyclingSettledCount = useMemo(
    () => cyclingSlateFixtures.filter((f) => f.status === 'SETTLED' || f.status === 'VOID').length,
    [cyclingSlateFixtures]
  );

  const upcomingFixtures = cyclingSlateFixtures.filter((f) => f.status === 'SCHEDULED' && f.kickoffAt > now);
  const haveFixtureDocs = cyclingSlateFixtures.length > 0;
  const minOpen = flexible ? 1 : picksK;
  const canStartNewEntry =
    selectedRound != null && isEntryEditable(selectedRound, [], now) && upcomingFixtures.length >= minOpen;
  const newEntryClosed = selEntry === null && selOpen && haveFixtureDocs && minOpen > 0 && !canStartNewEntry;

  const target = flexible ? Math.max(1, slateFixtures.length || picksK) : picksK;
  const picksIn = isCycling ? selEntry?.duelPicks?.length ?? 0 : selEntry?.predictions.length ?? 0;
  const remaining = Math.max(0, picksK - picksIn);

  const standings = group?.standings ?? [];
  const myRow = standings.find((r) => r.userId === uid) ?? null;
  const rankInGroup = myStand?.rankInGroup ?? myRow?.rankInGroup ?? null;
  const divisionSize = myStand?.divisionSize ?? group?.memberIds?.length ?? standings.length ?? 0;
  const roundScore = myRow?.roundScore ?? entry?.roundScore ?? 0;
  const movement: Movement | null = settled ? myRow?.movement ?? entry?.movement ?? null : null;

  const showLadder = !!entry;

  const pagerProps = {
    selectableRounds,
    selectedRoundId: selectedRound?.roundId ?? null,
    onSelectRound: (id: string) => {
      impact('selection');
      setPickedRoundId(id);
    }
  };

  // Football and cycling both build inline on the Compete tab. Settled/upcoming
  // rounds keep the compact summary card.
  const buildable = !isCycling && selOpen && selectedRound != null;
  const cyclingBuildable =
    isCycling &&
    selOpen &&
    selectedRound != null &&
    isEntryEditable(selectedRound, [], now);
  const [cyclingPickOpen, setCyclingPickOpen] = useState(false);
  useEffect(() => setCyclingPickOpen(false), [selectedRoundId]);
  useEffect(() => {
    if (duelFocus && cyclingBuildable) setCyclingPickOpen(true);
  }, [duelFocus, cyclingBuildable]);
  const pagerNode =
    selectableRounds.length > 1 ? (
      <SlatePager
        rounds={selectableRounds}
        selectedId={selectedRound?.roundId ?? null}
        isCycling={false}
        now={now}
        onSelect={pagerProps.onSelectRound}
      />
    ) : null;


  const clearFixtureFocus = () => {
    // Only churn the URL when a deep-link `fixture` param is actually set. Closing
    // the builder otherwise would fire a router replace on the same frame as the
    // sheet's slide-down, and that re-render competes with the dismiss (jank).
    if (!params.has('fixture')) return;
    const p = new URLSearchParams(params);
    p.delete('fixture');
    setParams(p, { replace: true });
  };

  const openFixture = (fixtureId: string, rid?: string) => {
    impact('light');
    const f = slateFixtures.find((x) => x.fixtureId === fixtureId);
    if (!f) return;
    const roundForLink = rid ?? selectedRound?.roundId ?? currentRoundId;

    if (isCycling) {
      const activeEntry = selEntry ?? entry ?? null;
      const hasCall = !!activeEntry?.duelPicks?.some((p) => p.duelId === fixtureId);
      const settled = f.status === 'SETTLED' || f.status === 'VOID';
      const racing =
        f.status === 'LIVE' || !!(selectedRound && !isEntryEditable(selectedRound, [], now));

      if (
        !hasCall &&
        !settled &&
        !racing &&
        selectedRound &&
        isEntryEditable(selectedRound, [], now)
      ) {
        navigate(cyclingHubPickHref(leagueId, { roundId: roundForLink, duelId: fixtureId }));
        return;
      }

      if (selectedRound) setLiveFixtureId(fixtureId);
      return;
    }

    const editable =
      !!selectedRound &&
      f.status === 'SCHEDULED' &&
      now < f.kickoffAt &&
      isEntryEditable(selectedRound, [], now);
    if (editable) {
      navigate(footballHubPickHref(leagueId, { roundId: roundForLink, fixtureId }));
      return;
    }

    if (selectedRound) setLiveFixtureId(fixtureId);
  };

  const closeCyclingPicks = () => {
    setCyclingPickOpen(false);
    if (params.has('duel')) {
      const p = new URLSearchParams(params);
      p.delete('duel');
      setParams(p, { replace: true });
    }
  };

  const handleCyclingPredict = (action: PicksCtaAction) => {
    if (action === 'picks' && cyclingBuildable) {
      impact('medium');
      setCyclingPickOpen(true);
      return;
    }
    onPredict(action, selectedRound?.roundId ?? undefined, !!selEntry);
  };

  // Play surface + division ladder: side-by-side from tablet up (narrow rail on iPad,
  // full rail on desktop). Phones stack unchanged.
  const playSurface = isCycling ? (
    <>
      <CyclingStageCard
        round={selectedRound}
        entry={selEntry}
        slateFixtures={cyclingSlateFixtures}
        settledCount={cyclingSettledCount}
        {...pagerProps}
        onPredict={handleCyclingPredict}
        onReorder={(next) => {
          if (!leagueId || !selectedRound?.roundId) return;
          const duelPicks = toDuelPicks(
            next.map((r) => ({ duelId: r.duelId, pick: r.pick })),
            CYCLING_WEIGHTS
          );
          if (!duelPicks) return;
          submitCycling.mutate({ leagueId, roundId: selectedRound.roundId, duelPicks });
        }}
      />
      {cyclingBuildable && selectedRound && (
        <CyclingPicksSheet
          key={selectedRound.roundId}
          open={cyclingPickOpen}
          onClose={closeCyclingPicks}
          leagueId={leagueId}
          round={selectedRound}
          fixtures={cyclingSlateFixtures}
          entry={selEntry}
          uid={uid}
          duelFocus={duelFocus}
        />
      )}
    </>
  ) : buildable ? (
    <BuildOverview
      key={selectedRound!.roundId}
      leagueId={leagueId}
      round={selectedRound!}
      fixtures={slateFixtures}
      entry={selEntry}
      uid={uid}
      nationalTeams={nationalTeams}
      scorerEnabled={scorerEnabled}
      statsEnabled={statsEnabled}
      pager={pagerNode}
      fixtureFocus={fixtureFocus}
      onClearFixtureFocus={clearFixtureFocus}
      onBoard={() => onPredict('board', selectedRound?.roundId ?? undefined, !!selEntry)}
    />
  ) : (
    <PicksCard
      round={selectedRound}
      nationalTeams={nationalTeams}
      settled={selSettled}
      open={selOpen}
      upcoming={selUpcoming}
      newEntryClosed={newEntryClosed}
      flexible={flexible}
      picksK={picksK}
      picksIn={picksIn}
      target={target}
      remaining={remaining}
      slateFixtures={slateFixtures}
      predictionByFixture={predictionByFixture}
      {...pagerProps}
      onPredict={(action) => onPredict(action, selectedRound?.roundId ?? undefined, !!selEntry)}
      onOpenFixture={openFixture}
    />
  );

  return (
    <>
      <DesktopColumns
        asideWidth="356px"
        asideWidthMd="248px"
        main={playSurface}
        aside={
          showLadder ? (
            <CompeteLadderSnap
              isCycling={isCycling}
              tierIndex={tierIndex}
              rankInGroup={rankInGroup}
              divisionSize={divisionSize}
              roundScore={roundScore}
              movement={movement}
              live={liveRound && !settled}
              liveProgress={liveProgress}
              standings={standings}
              uid={uid}
              onOpenStandings={onFullTable}
            />
          ) : undefined
        }
      />

      {!isCycling && selectedRound && (
        <LiveFixtureSheet
          openFixtureId={liveFixtureId}
          onClose={() => setLiveFixtureId(null)}
          leagueId={leagueId}
          round={selectedRound}
          fixtures={slateFixtures}
          entry={selEntry ?? entry}
          nationalTeams={nationalTeams}
        />
      )}

      {isCycling && selectedRound && (
        <LiveDuelSheet
          openDuelId={liveFixtureId}
          onClose={() => setLiveFixtureId(null)}
          leagueId={leagueId}
          round={selectedRound}
          fixtures={slateFixtures}
          entry={selEntry ?? entry}
        />
      )}
    </>
  );
}
